import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ClearButton from "../../ClearButton/ClearButton";
import { faPenToSquare, faTrash, faCheck, faXmark, faPlay } from '@fortawesome/free-solid-svg-icons';
// import styles from "../create-feedback/CreateFeedback.module.scss"; // REMOVED SCSS

const FeedbackList = ({
  annotations,
  clearAllFeedbacks,
  loading,
  editingCommentId,
  editedCommentText,
  setEditingCommentId,
  setEditedCommentText,
  handleUpdateComment,
  handleDeleteClick,
  handleAudioPlay,
  currentTime,
  showClearButton,
  hideHeader = false

}) => {
  const [expandedCommentId, setExpandedCommentId] = useState(null);
  const activeRef = useRef(null);

  const toggleComment = (id) => {
    setExpandedCommentId((prev) => (prev === id ? null : id));
  };

  const activeCommnet = (annotation) => {
    if (!annotation?.timeRange?.startTime) return false;
    return (
      currentTime >= annotation.timeRange.startTime &&
      currentTime <= annotation.timeRange.startTime + 0.3
    );
  };

  // Auto-scroll to active feedback
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [currentTime]);

  return (
    <div className="">
      {!hideHeader && (
        <div className="flex items-center justify-between sticky top-0 z-20 py-1 bg-white/80 backdrop-blur-sm">
          <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
            <span className="w-1 h-3 bg-[#237e45] rounded-full"></span>
            Feedback List
          </h4>
          {showClearButton && (
            <div className="transform scale-90 origin-right">
              <ClearButton clearAllFeedbacks={clearAllFeedbacks} />
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mb-2"></div>
          <p className="text-sm font-medium">Loading feedback...</p>
        </div>
      ) : annotations?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
          <p className="text-gray-400 font-medium">No feedback yet</p>
        </div>
      ) : (
        <div className="space-y-3 pb-4">
          {Array.from(annotations || [])
            .sort((a, b) => {
              const aActive = activeCommnet(a);
              const bActive = activeCommnet(b);
              if (aActive && !bActive) return -1;
              if (!aActive && bActive) return 1;
              return (a.timeRange?.startTime || 0) - (b.timeRange?.startTime || 0);
            })
            .map((annotation) => {
              const isActive = activeCommnet(annotation);
              const isExpanded = expandedCommentId === annotation._id;
              const isEditing = editingCommentId === annotation._id;
              const commentText = annotation?.comments?.[0]?.body || "No comment";
              const isAudio = commentText.startsWith("data:audio");

              return (
                <div
                  key={annotation._id || annotation.id}
                  ref={isActive ? activeRef : null}
                  className={`
                    group relative rounded-xl border transition-all duration-300 ease-in-out
                    ${isActive
                      ? "bg-green-50 border-green-500 shadow-lg ring-2 ring-green-500/30 translate-x-1"
                      : "bg-white border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-200"}
                  `}
                >
                  <div className="p-3">
                    <div className="flex items-start gap-3">
                      {/* Timestamp Badge */}
                      <div className={`
                        flex-shrink-0 px-2.5 py-1 rounded-md text-xs font-bold font-mono tracking-wide
                        ${isActive
                          ? "bg-green-600 text-white shadow-md"
                          : "bg-gray-100 text-gray-500 group-hover:bg-emerald-100 group-hover:text-emerald-700"}
                        transition-colors duration-200
                      `}>
                        {annotation?.timeRange?.startTime?.toFixed(2) || "0.00"}s
                      </div>

                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <div className="space-y-3">
                            <textarea
                              value={editedCommentText}
                              onChange={(e) => setEditedCommentText(e.target.value)}
                              className="w-full text-sm p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all shadow-sm resize-y min-h-[80px]"
                              placeholder="Edit your comment..."
                            />
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => handleUpdateComment(annotation)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition-colors shadow-sm"
                                title="Save"
                              >
                                <FontAwesomeIcon icon={faCheck} /> Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingCommentId(null);
                                  setEditedCommentText("");
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-semibold rounded-lg transition-colors shadow-sm"
                                title="Cancel"
                              >
                                <FontAwesomeIcon icon={faXmark} /> Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {isAudio ? (
                              <div className="bg-gray-50 rounded-lg p-2 border border-gray-100 flex items-center gap-3">
                                <div className="p-2 bg-emerald-100 text-emerald-600 rounded-full">
                                  <FontAwesomeIcon icon={faPlay} className="text-xs" />
                                </div>
                                <audio
                                  controls
                                  className="h-8 w-full max-w-[200px]"
                                  onPlay={(e) => handleAudioPlay(e.target, annotation)}
                                >
                                  <source src={commentText} type="audio/wav" />
                                  Your browser does not support the audio element.
                                </audio>
                              </div>
                            ) : (
                              <div className="relative">
                                <p className={`
                                  text-sm leading-relaxed whitespace-pre-line
                                  ${isActive ? "text-gray-900 font-medium" : "text-gray-600"}
                                `}>
                                  {isExpanded
                                    ? commentText
                                    : `${commentText.slice(0, 120)}${commentText.length > 120 ? "..." : ""}`}
                                </p>
                                {commentText.length > 120 && (
                                  <button
                                    onClick={() => toggleComment(annotation._id)}
                                    className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 mt-1 focus:outline-none"
                                  >
                                    {isExpanded ? "Show Less" : "Read More"}
                                  </button>
                                )}
                              </div>
                            )}

                            {/* Actions Toolbar */}
                            {handleUpdateComment && isActive && !isEditing && (
                              <div className="flex items-center gap-2 pt-2 mt-1 border-t border-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                {!isAudio && (
                                  <button
                                    onClick={() => {
                                      setEditingCommentId(annotation._id);
                                      setEditedCommentText(annotation.comments[0].body);
                                    }}
                                    className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                                  >
                                    <FontAwesomeIcon icon={faPenToSquare} /> Edit
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteClick(annotation)}
                                  className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                >
                                  <FontAwesomeIcon icon={faTrash} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default FeedbackList;